/** 접수중 연차 신청 날짜 목록 문자열 */
export function formatPendingLeaveDetail(pendingLeaveApplications) {
  if (!pendingLeaveApplications?.length) return '';
  return pendingLeaveApplications
    .map((app) => {
      const dates = (app.items || [])
        .map((item) => `${item.date}(${item.hours}시간)`)
        .join(', ');
      return dates;
    })
    .filter(Boolean)
    .join(' / ');
}

/** 참여자 1명의 정산 차단 사유 (일괄 모달 표용) */
export function getParticipantBlockReasonRows(p) {
  const rows = [];

  if (p.hasMissingEndDate) {
    rows.push({
      key: 'missingEndDate',
      label: '참여종료일 미입력',
      detail: '참여자 관리에서 종료일을 입력한 후 다시 시도해 주세요.',
    });
  }

  if (p.hasPendingLeave && p.pendingLeaveApplications?.length > 0) {
    const count = p.pendingLeaveApplications.length;
    const totalHours = p.pendingLeaveApplications.reduce(
      (sum, app) => sum + (app.totalHours || 0),
      0
    );
    rows.push({
      key: 'pendingLeave',
      label: '접수중 연차',
      detail: `${count}건(총 ${totalHours}시간) — ${formatPendingLeaveDetail(p.pendingLeaveApplications)}`,
    });
  }

  if (p.invalidAbsenceDates?.length > 0) {
    rows.push({
      key: 'absence',
      label: '기간 밖 결근',
      detail: p.invalidAbsenceDates
        .map((item) => `${item.date}(${item.month}월)`)
        .join(', '),
    });
  }

  if (p.invalidLeaveDates?.length > 0) {
    rows.push({
      key: 'leave',
      label: '기간 밖 연차',
      detail: p.invalidLeaveDates
        .map((item) => `${item.date}(${item.hours}시간)`)
        .join(', '),
    });
  }

  return rows;
}

/** 일괄·지연 정산 모달 상단 요약 */
export function summarizeBatchSettleBlock(invalidParticipants) {
  if (!invalidParticipants?.length) {
    return { headline: '', bullets: [], actionHint: '' };
  }

  const missingEnd = invalidParticipants.filter((p) => p.hasMissingEndDate);
  const pendingOnly = invalidParticipants.filter(
    (p) => p.hasPendingLeave && !p.hasPeriodViolation && !p.hasMissingEndDate
  );
  const periodOnly = invalidParticipants.filter(
    (p) => p.hasPeriodViolation && !p.hasPendingLeave && !p.hasMissingEndDate
  );
  const mixed = invalidParticipants.filter((p) => {
    const flags = [p.hasMissingEndDate, p.hasPendingLeave, p.hasPeriodViolation].filter(Boolean);
    return flags.length >= 2;
  });

  const nameList = (list) => list.map((p) => p.name).join(', ');

  const bullets = [];

  if (missingEnd.length > 0) {
    bullets.push({
      type: 'missingEndDate',
      text: `참여종료일 미입력 (${missingEnd.length}명): ${nameList(missingEnd)}`,
    });
  }

  const pendingAll = invalidParticipants.filter((p) => p.hasPendingLeave);
  if (pendingAll.length > 0 && pendingOnly.length === 0 && mixed.length === 0 && missingEnd.length === 0) {
    bullets.push({
      type: 'pendingOnly',
      text: `접수중인 연차가 있어 정산할 수 없습니다 (${pendingAll.length}명): ${nameList(pendingAll)}`,
    });
  } else if (pendingAll.length > 0) {
    bullets.push({
      type: 'pending',
      text: `접수중 연차 (${pendingAll.length}명): ${nameList(pendingAll)}`,
    });
  }

  const periodAll = invalidParticipants.filter((p) => p.hasPeriodViolation);
  if (periodAll.length > 0 && periodOnly.length === 0 && mixed.length === 0 && missingEnd.length === 0 && pendingAll.length === 0) {
    bullets.push({
      type: 'periodOnly',
      text: `참여 기간을 벗어난 결근·연차 사용 (${periodAll.length}명): ${nameList(periodAll)}`,
    });
  } else if (periodAll.length > 0) {
    bullets.push({
      type: 'period',
      text: `기간 밖 결근·연차 (${periodAll.length}명): ${nameList(periodAll)}`,
    });
  }

  let headline = `다음 ${invalidParticipants.length}명은 정산을 진행할 수 없습니다.`;
  if (bullets.length === 1 && pendingOnly.length === invalidParticipants.length) {
    headline = '접수중인 연차가 있어 정산할 수 없습니다.';
  } else if (bullets.length === 1 && periodOnly.length === invalidParticipants.length) {
    headline = '참여 기간을 벗어난 데이터가 있어 정산할 수 없습니다.';
  } else if (bullets.length === 1 && missingEnd.length === invalidParticipants.length) {
    headline = '참여종료일이 없어 정산할 수 없습니다.';
  }

  return {
    headline,
    bullets,
    actionHint:
      '승인·반려·접수취소, 참여종료일 입력, 또는 기간 밖 데이터 수정 후 다시 정산을 시도해 주세요.',
  };
}

/** 개별 정산 alert 본문 */
export function buildIndividualSettleBlockAlert(data) {
  const sections = [];

  if (data.hasPendingLeave && data.pendingLeaveApplications?.length > 0) {
    const count = data.pendingLeaveApplications.length;
    const totalHours = data.pendingLeaveApplications.reduce(
      (sum, app) => sum + (app.totalHours || 0),
      0
    );
    let block = `【접수중 연차】\n`;
    block += `승인 대기 중인 연차 신청이 ${count}건(총 ${totalHours}시간) 있습니다.\n`;
    data.pendingLeaveApplications.forEach((app) => {
      const dates = (app.items || [])
        .map((item) => `${item.date}(${item.hours}시간)`)
        .join(', ');
      block += `· ${dates}\n`;
    });
    block += '승인·반려·접수취소 후 다시 시도해 주세요.';
    sections.push(block);
  }

  if (data.invalidAbsenceDates?.length > 0 || data.invalidLeaveDates?.length > 0) {
    let block = `【참여 기간 위반】\n`;
    block += `참여 기간(${data.startDate} ~ ${data.endDate})을 벗어난 데이터가 있습니다.\n\n`;

    if (data.invalidAbsenceDates?.length > 0) {
      block += '[결근일]\n';
      data.invalidAbsenceDates.forEach((item) => {
        block += `· ${item.date} (${item.month}월)\n`;
      });
      block += '\n';
    }

    if (data.invalidLeaveDates?.length > 0) {
      block += '[기간 밖 연차]\n';
      data.invalidLeaveDates.forEach((item) => {
        block += `· ${item.date} (${item.hours}시간)\n`;
      });
    }
    sections.push(block.trim());
  }

  if (sections.length === 0) {
    return '정산을 진행할 수 없습니다.';
  }

  return `${sections.join('\n\n')}\n\n정산을 진행할 수 없습니다.`;
}

export function getBirthDateFromRrn(rrn) {
  if (!rrn || rrn.length < 6) return '-';
  const year = rrn.substring(0, 2);
  const month = rrn.substring(2, 4);
  const day = rrn.substring(4, 6);
  const century = parseInt(year, 10) <= 30 ? '20' : '19';
  return `${century}${year}-${month}-${day}`;
}
